/**
 * Council Trail v0 — WebSocket client with auto-reconnect.
 *
 * Plan rev 2 defensive contracts:
 * - D-5.6 — exponential backoff reconnect (1s → 2s → 4s → 8s → 16s → 30s cap)
 *   + auto-reauth on close-code 4401 (session-cookie-expired). Without this,
 *   a single network blip drops the WS forever; every WS-connected user
 *   loses Council until full page reload.
 *
 * Subscribers receive PeerEventWire payloads from the backend bus. The
 * client also exposes sendLensChange so the operator's lens transitions
 * emit OPERATOR_FOCUSED events server-side.
 */
import type { PeerEventWire, EventLogEntryFrame } from '../types/council';

/** Any frame the council WS can deliver: a real PeerEvent or a G3a log envelope. */
export type CouncilFrame = PeerEventWire | EventLogEntryFrame;
type Listener = (event: CouncilFrame) => void;

const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_BACKOFF_MS = 30_000;

export class CouncilWsClient {
  private connections: Map<string, WebSocket> = new Map();
  private listeners: Map<string, Set<Listener>> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private reconnectTimers: Map<string, number> = new Map();
  private manualClose: Set<string> = new Set();

  subscribe(sessionId: string, listener: Listener): () => void {
    if (!this.listeners.has(sessionId)) this.listeners.set(sessionId, new Set());
    this.listeners.get(sessionId)!.add(listener);
    this.manualClose.delete(sessionId);
    if (!this.connections.has(sessionId)) this.connect(sessionId);
    return () => {
      this.listeners.get(sessionId)?.delete(listener);
      if (this.listeners.get(sessionId)?.size === 0) {
        this.manualClose.add(sessionId);
        this.cancelReconnect(sessionId);
        this.connections.get(sessionId)?.close();
        this.connections.delete(sessionId);
      }
    };
  }

  private connect(sessionId: string): void {
    const wsBase = window.location.origin.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/ws/sessions/${sessionId}/events`);

    ws.onopen = () => {
      this.reconnectAttempts.set(sessionId, 0);
      // v0.1 fix — events broadcast while we were disconnected are
      // lost forever (the backend bus has no replay). Fire a synthetic
      // ws_connected event so subscribers can refetch state on every
      // reconnect (and on initial connect) to catch up.
      // SP3 G.1: ws_connected is now a real PeerEventType literal — drop
      // the cast.
      this.listeners.get(sessionId)?.forEach((l) =>
        l({
          event_type: 'ws_connected',
          session_id: sessionId,
          timestamp: new Date().toISOString(),
          source_persona_id: null,
          target: {},
          trail_id: null,
          cascade_depth: 0,
          correlation_id: '',
        }),
      );
    };

    ws.onmessage = (e: MessageEvent) => {
      try {
        const event: CouncilFrame = JSON.parse(e.data);
        this.listeners.get(sessionId)?.forEach(l => l(event));
      } catch {
        // drop malformed frame
      }
    };

    ws.onclose = (closeEvent: CloseEvent) => {
      this.connections.delete(sessionId);
      if (this.manualClose.has(sessionId)) return;

      if (closeEvent.code === 4401) {
        void this.precheckAndReconnect(sessionId);
        return;
      }

      this.scheduleReconnect(sessionId);
    };

    this.connections.set(sessionId, ws);
  }

  private async precheckAndReconnect(sessionId: string): Promise<void> {
    try {
      const r = await fetch('/api/sessions/me', { credentials: 'same-origin' });
      if (r.ok) {
        this.scheduleReconnect(sessionId);
        return;
      }
    } catch {
      // network blip — fall through to backoff
    }
    // Session truly expired — emit synthetic event for UI consumers.
    this.listeners.get(sessionId)?.forEach(l =>
      l({
        event_type: 'session_expired' as unknown as PeerEventWire['event_type'],
        session_id: sessionId,
        timestamp: new Date().toISOString(),
        source_persona_id: null,
        target: {},
        trail_id: null,
        cascade_depth: 0,
        correlation_id: '',
      })
    );
  }

  private scheduleReconnect(sessionId: string): void {
    const attempt = this.reconnectAttempts.get(sessionId) ?? 0;
    const delay = RECONNECT_DELAYS_MS[
      Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
    ] ?? MAX_BACKOFF_MS;
    this.reconnectAttempts.set(sessionId, attempt + 1);
    const timerId = window.setTimeout(() => {
      this.reconnectTimers.delete(sessionId);
      if (!this.manualClose.has(sessionId)) this.connect(sessionId);
    }, delay);
    this.reconnectTimers.set(sessionId, timerId);
  }

  private cancelReconnect(sessionId: string): void {
    const t = this.reconnectTimers.get(sessionId);
    if (t !== undefined) window.clearTimeout(t);
    this.reconnectTimers.delete(sessionId);
    this.reconnectAttempts.delete(sessionId);
  }

  sendLensChange(
    sessionId: string,
    payload: {
      mode: string;
      active_persona_ids: string[];
      active_trail_id: string | null;
    },
  ): void {
    this.connections.get(sessionId)?.send(JSON.stringify({
      type: 'lens_change',
      ...payload,
    }));
  }

  /** Test-only helper: drop all state so each test starts clean. */
  __resetForTests(): void {
    for (const ws of Array.from(this.connections.values())) ws.close();
    this.connections.clear();
    this.listeners.clear();
    this.reconnectAttempts.clear();
    for (const t of Array.from(this.reconnectTimers.values())) window.clearTimeout(t);
    this.reconnectTimers.clear();
    this.manualClose.clear();
  }
}

export const councilWsClient = new CouncilWsClient();
